import * as React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CloseEmptyIcon } from '@align-cpf/uikit-icons';
import { CssBaseline } from '../../atoms/CssBaseline';
import { Icon } from '../Icon';
import { createLocators } from '@u1/locators';
import { createPortal } from 'react-dom';
import { modalWindow } from '../../../animation';
import { useCssBaselineContext } from '../CssBaseline/CssBaseline.context';
import { useOnClickOutside } from '../../../hooks';
import { useRootContext } from '../Root/Root.context';
import clsx from 'clsx';
import useStyles from './Modal.styles';

export interface IModalProps {
  /**
   * Custom header component
   */
  header?: React.ReactNode;
  /**
   * Custom footer component
   */
  footer?: React.ReactNode;
  /**
   * On close callback
   */
  onClose: () => unknown;
  /**
   * Is modal open
   */
  isOpen: boolean;
  /**
   * Maximum modal height. In case of overflow scroll is applied to the content
   */
  maxHeight?: string;
  /**
   * Maximum modal width
   */
  maxWidth?: string;
  /**
   * Modal root node. document.body by default
   */
  root?: ShadowRoot | HTMLElement;
  /**
   * Escape hatch to add styles to the option
   */
  _UNSAFE_classname?: string;
}

export const modalLocators = createLocators({
  name: 'Modal',
  componentLocators: [
    'modal',
    'background',
    'header',
    'close',
    'closeIcon',
    'content',
    'footer',
  ],
});
const { locators } = modalLocators();

const useDomNode = (root?: ShadowRoot | HTMLElement) => {
  const [domNode, setDomNode] = React.useState<HTMLElement | undefined>();
  React.useEffect(() => {
    const node = document.createElement('div');
    node.style.position = 'relative';
    node.style.zIndex = '1000';
    // rely on document.body only inside useEffect
    const elementToAppend = root || document.body;
    elementToAppend.appendChild(node);
    setDomNode(node);

    return () => {
      elementToAppend.removeChild(node);
    };
  }, []);
  return domNode;
};

const Modal: React.FC<React.PropsWithChildren<IModalProps>> = ({
  children,
  header,
  footer,
  onClose,
  isOpen,
  _UNSAFE_classname,
  maxHeight,
  maxWidth = 500,
  root,
}) => {
  const rootContext = useRootContext();
  const classes = useStyles({ maxHeight });
  const domNode = useDomNode(rootContext?.node || root);

  const ref = useOnClickOutside<HTMLDivElement>(() => {
    isOpen && onClose();
  });

  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isOpen]);

  const renderHeader = () => (
    <div
      {...locators.header()}
      className={clsx(classes.header, {
        [classes.withHeader]: header,
      })}
    >
      {header}
      <div
        {...locators.close()}
        onClick={onClose}
        className={classes.closeIcon}
      >
        <Icon
          icon={CloseEmptyIcon}
          locatorsOverrides={locators.closeIcon()}
          size={18}
        />
      </div>
    </div>
  );

  const renderFooter = () => (
    <div {...locators.footer()} className={clsx(classes.footer)}>
      {footer}
    </div>
  );

  const { theme } = useCssBaselineContext();

  const component = (
    <CssBaseline theme={theme}>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            {...locators.background()}
            key={'background'}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={clsx(classes.root)}
          >
            <motion.div
              {...locators.modal({ isOpen })}
              key={'modal'}
              initial={modalWindow.hidden}
              animate={modalWindow.visible}
              exit={modalWindow.hidden}
              ref={ref}
              className={clsx({
                [classes.modal]: true,
                [classes.isOpen]: isOpen,
              })}
              style={{
                maxWidth,
                maxHeight,
              }}
            >
              {renderHeader()}
              <div
                {...locators.content()}
                className={clsx(classes.content, _UNSAFE_classname)}
              >
                {children}
              </div>
              {footer && renderFooter()}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </CssBaseline>
  );

  if (!domNode) {
    return null;
  }

  return createPortal(component, domNode);
};

export default Modal;
